import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Wand2, User, Briefcase, MapPin, GraduationCap, Target, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

const EMPTY = {
  full_name: "",
  current_title: "",
  years_experience: "",
  skills: [],
  industries: [],
  education: "",
  certifications: [],
  location_preference: "",
  target_roles: [],
  seniority: "",
  expected_salary: "",
  keywords: [],
};

const SENIORITY_OPTIONS = ["entry", "mid", "senior", "lead", "executive"];

function ChipInput({ label, value, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(value) ? value : [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (arr.includes(v)) { setDraft(""); return; }
    onChange([...arr, v]);
    setDraft("");
  };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-2 flex flex-wrap gap-2 mb-2">
        {arr.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-2 rounded-full bg-[rgba(157,0,255,0.14)] text-[#E4CBFF] border border-[rgba(157,0,255,0.28)] px-3 py-1 text-xs font-semibold">
            {t}
            <button data-testid={`${testId}-remove-${i}`} onClick={() => remove(i)} className="text-[#E4CBFF]/70 hover:text-white">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`${testId}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[var(--cavilo-blue)]"
        />
        <button
          data-testid={`${testId}-add`}
          onClick={add}
          className="btn-neon text-sm !py-2"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export default function CareerProfile() {
  const [profile, setProfile] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoExtracted, setAutoExtracted] = useState(false);
  const [params] = useSearchParams();
  const focusMissing = params.get("focus") === "missing";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/career-profile");
      if (r.data?.profile) {
        setProfile({ ...EMPTY, ...r.data.profile });
        setAutoExtracted(!!r.data.auto_extracted);
      } else {
        setProfile(EMPTY);
      }
    } catch {
      toast.error("Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const strength = useMemo(() => {
    const weights = [
      ["full_name", 10], ["current_title", 15], ["years_experience", 10],
      ["skills", 20], ["industries", 5], ["education", 10],
      ["location_preference", 10], ["target_roles", 10], ["seniority", 5], ["keywords", 5],
    ];
    let score = 0;
    for (const [k, w] of weights) {
      const v = profile[k];
      if (Array.isArray(v) ? v.length > 0 : (v && String(v).trim())) score += w;
    }
    return Math.min(100, score);
  }, [profile]);

  const strengthColor = strength >= 70 ? "#10B981" : strength >= 40 ? "#F59E0B" : "#EF4444";
  const strengthLabel = strength >= 70 ? "Strong" : strength >= 40 ? "Getting there" : "Needs detail";

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/career-profile", profile);
      toast.success("Profile saved");
      setAutoExtracted(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-slate-500" data-testid="career-profile-loading">Loading your profile…</div>;
  }

  return (
    <div className="space-y-8" data-testid="career-profile-root">
      <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="chip mb-3"><Sparkles size={12} /> Career Profile</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <span className="text-neon-purple">Your career, at a glance.</span>
          </h1>
          <p className="mt-3 text-slate-400 text-lg max-w-2xl leading-relaxed">
            The Job Agent uses this profile to score matches. The more we know, the sharper your matches.
          </p>
        </div>
        <Link
          to="/job-agent"
          data-testid="go-job-agent"
          className="btn-neon inline-flex items-center gap-2"
        >
          See matched jobs <ArrowRight size={16} />
        </Link>
      </div>

      {autoExtracted && (
        <div className="glass p-4 text-sm text-[#FFD166] flex items-start gap-3" data-testid="auto-extracted-banner">
          <Wand2 size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-white">We pre-filled this from your latest resume.</div>
            <div className="opacity-80 mt-1 text-slate-400">Review each field and hit save to lock it in.</div>
          </div>
        </div>
      )}

      {focusMissing && !loading && (
        <div className="glass p-4 text-sm flex items-start gap-3" data-testid="focus-missing-banner">
          <Sparkles size={18} className="mt-0.5 text-[#00E5FF] shrink-0" />
          <div>
            <div className="font-bold text-white">Complete these missing details</div>
            <div className="mt-1 text-slate-300">
              We&apos;ve auto-filled what we could from your resume. Fields highlighted with a cyan ring still need your attention.
            </div>
          </div>
        </div>
      )}

      {/* Strength meter */}
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Profile strength</div>
          <div className="text-sm font-bold" style={{ color: strengthColor }} data-testid="profile-strength-value">{strength}% · {strengthLabel}</div>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${strength}%`, background: `linear-gradient(90deg, ${strengthColor}, #00E5FF)`, boxShadow: `0 0 12px ${strengthColor}88` }} />
        </div>
      </div>

      {/* Basics */}
      <section className="glass p-6 space-y-5">
        <div className="flex items-center gap-2 text-white font-display font-bold text-lg"><User size={18} /> Basics</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name" testId="cp-full-name">
            <input
              data-testid="cp-full-name"
              value={profile.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
              placeholder="Alex Cavilow"
            />
          </Field>
          <Field label="Current title" testId="cp-current-title">
            <input
              data-testid="cp-current-title"
              value={profile.current_title}
              onChange={(e) => set("current_title", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
              placeholder="Senior Product Manager"
            />
          </Field>
          <Field label="Years of experience" testId="cp-years">
            <input
              data-testid="cp-years"
              value={profile.years_experience}
              onChange={(e) => set("years_experience", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
              placeholder="5"
            />
          </Field>
          <Field label="Seniority" testId="cp-seniority">
            <select
              data-testid="cp-seniority"
              value={profile.seniority}
              onChange={(e) => set("seniority", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] bg-transparent"
            >
              <option value="">Select…</option>
              {SENIORITY_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </Field>
        </div>
      </section>

      {/* Skills & industries */}
      <section className="glass p-6 space-y-5">
        <div className="flex items-center gap-2 text-white font-display font-bold text-lg"><Briefcase size={18} /> Skills & industries</div>
        <ChipInput label="Skills" value={profile.skills} onChange={(v) => set("skills", v)} placeholder="e.g. SQL, Product strategy…" testId="cp-skills" />
        <ChipInput label="Industries" value={profile.industries} onChange={(v) => set("industries", v)} placeholder="e.g. Fintech, SaaS…" testId="cp-industries" />
        <ChipInput label="Keywords (for ATS matching)" value={profile.keywords} onChange={(v) => set("keywords", v)} placeholder="e.g. Growth, Roadmap…" testId="cp-keywords" />
      </section>

      {/* Location & targets */}
      <section className="glass p-6 space-y-5">
        <div className="flex items-center gap-2 text-white font-display font-bold text-lg"><MapPin size={18} /> Location & goals</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Location preference" testId="cp-location">
            <input
              data-testid="cp-location"
              value={profile.location_preference}
              onChange={(e) => set("location_preference", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
              placeholder="Dubai, UAE or Remote"
            />
          </Field>
          <Field label="Expected salary" testId="cp-salary">
            <input
              data-testid="cp-salary"
              value={profile.expected_salary}
              onChange={(e) => set("expected_salary", e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
              placeholder="AED 30,000 / month"
            />
          </Field>
        </div>
        <ChipInput label="Target roles" value={profile.target_roles} onChange={(v) => set("target_roles", v)} placeholder="e.g. Product Manager, Growth Lead…" testId="cp-targets" />
      </section>

      {/* Education & certs */}
      <section className="glass p-6 space-y-5">
        <div className="flex items-center gap-2 text-white font-display font-bold text-lg"><GraduationCap size={18} /> Education & certifications</div>
        <Field label="Education" testId="cp-education">
          <input
            data-testid="cp-education"
            value={profile.education}
            onChange={(e) => set("education", e.target.value)}
            className="w-full rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-slate-500"
            placeholder="B.Tech Computer Science, IIT Bombay"
          />
        </Field>
        <ChipInput label="Certifications" value={profile.certifications} onChange={(v) => set("certifications", v)} placeholder="e.g. PMP, AWS SAA…" testId="cp-certs" />
      </section>

      <div className="flex items-center justify-end gap-3 sticky bottom-0 py-4" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(3,3,8,0.95) 40%)" }}>
        <button
          data-testid="cp-save"
          onClick={save}
          disabled={saving}
          className="btn-neon inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Save size={16} /> {saving ? "Saving…" : "Improve Profile Strength"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">{label}</label>
      {children}
    </div>
  );
}
